import { useUser } from '@folklore/auth';
import { useRouteMatcher, useRoutes, useUrlGenerator } from '@folklore/routes';
import { useTracking } from '@folklore/tracking';
import { animated, useTransition } from '@react-spring/web';
import classNames from 'classnames';
import queryString from 'query-string';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Redirect, useLocation, useRoute, useSearch } from 'wouter';

import useScrollEnter from '../../hooks/useScrollEnter';
import { usePageViewTracking } from '../../hooks/useTracking';

import { useRequestBase } from '../../contexts/RequestContext';
import { useDefaultBrand, useSetting } from '../../contexts/SiteContext';
import BasicButton from '../buttons/BasicButton';
import BrandLogoButton from '../buttons/BrandLogoButton';
import SsoButton from '../buttons/SsoButton';
import LoginForm from '../forms/LoginForm';
import HeaderDescription from '../typography/HeaderDescription';
import HeaderTitle from '../typography/HeaderTitle';

import styles from '../../styles/pages/login-page.module.css';

type LoginPageProps = {
    entered?: boolean;
    entering?: boolean;
    leaving?: boolean;
    current?: boolean;
    className?: string | null;
    contentClassName?: string | null;
    onEnter?: ((...args: unknown[]) => void) | null;
    onLeave?: ((...args: unknown[]) => void) | null;
};

function LoginPage({
    entered = false,
    entering = false,
    leaving = false,
    current = false,
    className = null,
    contentClassName = null,
    onEnter = null,
    onLeave = null,
}: LoginPageProps) {
    const ssoButtons = useSetting('ssoButtons', ['google', 'facebook']);
    const user = useUser();
    const url = useUrlGenerator();
    const [location, setLocation] = useLocation();
    const defaultBrand = useDefaultBrand();

    const routes = useRoutes();
    const routeMatcher = useRouteMatcher();
    const [isRegister = false] = useRoute(routes.register);
    const tracking = useTracking();
    const { confirmed = false } = user || {};

    const [verifyMethod, setVerifyMethod] = useState(null);

    useScrollEnter({
        disabled: !current,
        onEnter,
        onLeave,
    });

    const locationSearch = useSearch();
    const baseUri = useRequestBase();
    const { next: nextUrl = null } = queryString.parse(locationSearch) || {};
    const absoluteNextUrl = `${baseUri}${nextUrl || url('account')}`;

    const onLoginComplete = () => {
        if (nextUrl !== null && nextUrl.match(/^https?:\/\//)) {
            window.location.href = nextUrl;
            return;
        }
        setLocation(nextUrl || url('account'));
    };

    const onVerify = (newMethod) => {
        setVerifyMethod(newMethod);
    };

    const onCancelVerify = () => {
        setVerifyMethod(null);
    };

    const onClickSso = (provider) => {
        tracking.trackEvent('Auth', 'click_sso', provider);
    };

    const transitions = useTransition([location], {
        keys: (id) => `${id}`,
        initial: {
            x: '0%',
            opacity: 1,
        },
        from: {
            x: '100%',
            opacity: 0,
        },
        enter: {
            x: '0%',
            opacity: 1,
        },
        leave: {
            x: '-100%',
            opacity: 0,
        },
    });

    usePageViewTracking(current && user === null, {
        pageType: 'auth',
    });

    if (user !== null && confirmed && current) {
        return <Redirect to={nextUrl || url('account')} replace />;
    }

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.entered]: entered,
                    [styles.entering]: entering,
                    [styles.leaving]: leaving,
                    [styles.isRegister]: isRegister,
                    [styles.isVerifying]: verifyMethod !== null,
                },
                className,
                contentClassName,
            ])}
            data-brand="urbania"
            data-theme="account"
        >
            <BrandLogoButton theme="light" brand={defaultBrand} className={styles.urbania} />

            {transitions((style, transitionLocation, { phase }) => {
                const [transitionIsRegister = false] = routeMatcher(
                    routes.register,
                    transitionLocation,
                );
                return (
                    <animated.div
                        className={classNames([styles.wrapper, [styles[phase]]])}
                        style={style}
                    >
                        <div className={styles.inner}>
                            <HeaderTitle className={styles.title}>
                                {transitionIsRegister ? (
                                    <FormattedMessage
                                        defaultMessage="Inscrivez-vous"
                                        description="Page title"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Connectez-vous"
                                        description="Page title"
                                    />
                                )}
                            </HeaderTitle>
                            <HeaderDescription className={styles.description}>
                                {verifyMethod !== null ? (
                                    <FormattedMessage
                                        defaultMessage="Pour valider votre identité, nous vous avons envoyé un code de vérification par {method}."
                                        description="Login description"
                                        values={{
                                            method:
                                                verifyMethod === 'phone' ? (
                                                    <FormattedMessage
                                                        defaultMessage="SMS"
                                                        description="Method"
                                                    />
                                                ) : (
                                                    <FormattedMessage
                                                        defaultMessage="courriel"
                                                        description="Method"
                                                    />
                                                ),
                                        }}
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Entrez votre courriel ou votre numéro de téléphone pour accéder à votre profil, votre historique ou gérer vos abonnements."
                                        description="Login description"
                                    />
                                )}
                            </HeaderDescription>
                            <LoginForm
                                onComplete={onLoginComplete}
                                onVerify={onVerify}
                                onCancelVerify={onCancelVerify}
                                fieldClassName={styles.field}
                                className={styles.form}
                            />
                            <div className={styles.ssoContainer}>
                                <div className={styles.continue}>
                                    <div className={styles.line} />
                                    <HeaderDescription className={styles.label}>
                                        <FormattedMessage
                                            defaultMessage="ou continuer avec"
                                            description="Form label"
                                        />
                                    </HeaderDescription>
                                    <div className={styles.line} />
                                </div>
                                <div className={styles.ssoButtons}>
                                    {ssoButtons.map((provider) => (
                                        <SsoButton
                                            key={`sso-${provider}`}
                                            provider={provider}
                                            source="login"
                                            theme="light"
                                            nextUrl={absoluteNextUrl}
                                            className={styles.ssoButton}
                                            onClick={() => onClickSso(provider)}
                                            onComplete={onLoginComplete}
                                        />
                                    ))}
                                </div>
                            </div>
                            <HeaderDescription className={styles.toggleContainer}>
                                {transitionIsRegister ? (
                                    <FormattedMessage
                                        defaultMessage="Déjà abonné?"
                                        description="Label"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Pas de compte?"
                                        description="Label"
                                    />
                                )}
                                <BasicButton
                                    href={transitionIsRegister ? url('login') : url('subscription')}
                                    className={styles.toggle}
                                >
                                    {transitionIsRegister ? (
                                        <FormattedMessage
                                            defaultMessage="Connectez-vous"
                                            description="Label"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            defaultMessage="Abonnez-vous"
                                            description="Label"
                                        />
                                    )}
                                </BasicButton>
                            </HeaderDescription>
                        </div>
                    </animated.div>
                );
            })}
        </div>
    );
}

export default LoginPage;
